import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import {
  AddCircleOutlineOutlined,
  Close,
} from "@mui/icons-material";

import RichTextEditor from "../../../components/common/RichTextEditor";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";

interface NotesCardProps {
  streamId?: number;
}

interface StreamResponse {
  id: number;
  stream_evaluation_summary: string | null;
  title?: string;
  date?: string;
  address_one?: string;
  address_two?: string;
  suburb?: string;
  city?: string;
  booking?: {id: number;};
}

const NotesCard: React.FC<NotesCardProps> = ({ streamId }) => {
  const [open, setOpen] = useState(false);
  const [notesContent, setNotesContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const currentUser = useSelector(
  (state: RootState) => state.auth.user
);

  const [streamData, setStreamData] = useState<StreamResponse | null>(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        if (!streamId) return;

        const res = await apiService.get<StreamResponse>(
          `/api/streams/stream/${streamId}/`
        );

        setNotesContent(
          res.data.stream_evaluation_summary || null
        );

        setStreamData(res.data);
      } catch (error) {
        console.error("Failed to fetch notes:", error);
        toast.error("Failed to fetch notes");
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [streamId]);

  /**
   * Save or update notes
   */
  const handleSaveNotes = async (htmlContent: string) => {
    try {
      const payload = {
        stream_evaluation_summary: htmlContent,
      };

      await apiService.patch(
        `/api/streams/stream/${streamId}/`,
        payload
      );

      setNotesContent(htmlContent);

      toast.success(
        notesContent
          ? "Notes updated successfully"
          : "Notes added successfully"
      );

      handleClose();
    } catch (error) {
      console.error("Failed to update notes:", error);
      toast.error("Failed to save notes. Please try again.");
    }
  };

  /**
 * Open WhatsApp with notes + stream details
 */
const handleSendToWhatsApp = () => {
  try {
    if (!notesContent || !notesContent.trim()) {
      toast.error("No notes available to send");
      return;
    }

    setSendingWhatsApp(true);

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = notesContent;

    const plainText =
      tempDiv.textContent || tempDiv.innerText || "";

    const formattedDate = streamData?.date
      ? new Date(streamData.date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "";


    
    const formattedAddress = [
      streamData?.address_one,
      streamData?.address_two,
      streamData?.suburb,
      streamData?.city,
    ]
      .filter((v) => v && v !== "N/A")
      .join(", ");


    const baseUrl = window.location.origin;

    const streamLink = `${baseUrl}/stream-management/view/${streamData?.id}`;

    const bookingLink = streamData?.booking?.id
      ? `${baseUrl}/booking-management/view/${streamData.booking.id}`
      : "";


    
      const lines = [
    "*Stream Details*",
    "",
    `*Title:* ${streamData?.title ?? ""}`,
    `*Stream Date:* ${formattedDate}`,
    `*Service Address:* ${formattedAddress}`,
    "",
    "*Stream Link:*",
    streamLink,
      ];

      if (bookingLink) {
        lines.push(
          "",
          "*Booking Link:*",
          bookingLink
        );
      }

    lines.push(
      "",
      "*Event Notes*",
      "",
      plainText,
      "",
      "*Forwarded By:*",
      `${currentUser?.first_name ?? ""} ${currentUser?.last_name ?? ""}`,
      currentUser?.username ?? "",
      "",
      "Sent from MMS"
    );

    const formattedMessage = lines.join("\n");

    const encodedMessage = encodeURIComponent(formattedMessage.normalize("NFC"));
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;

    window.open(
      whatsappUrl,
      "_blank",
      "noopener,noreferrer"
    );

    toast.success("WhatsApp opened successfully");

  } catch (error) {
    console.error("Failed to open WhatsApp:", error);
    toast.error("Failed to open WhatsApp");
  } finally {
    setSendingWhatsApp(false);
  }
};
  return (
    <Grid>
      <Card sx={{ boxShadow: 0, height: "auto", mt: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Event Notes
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleSendToWhatsApp}
                disabled={!notesContent || sendingWhatsApp}
                sx={{ mr: 1 }}
              >
                {sendingWhatsApp ? "Opening..." : "Send to WhatsApp"}
              </Button>

              <IconButton onClick={handleOpen}>
                <AddCircleOutlineOutlined />
              </IconButton>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading notes...
              </Typography>
            ) : notesContent ? (
              <Grid size={{ xs: 12 }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: notesContent,
                  }}
                />
              </Grid>
            ) : (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  No notes have been added yet.
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Dialog for editing notes */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DialogTitle>
            {notesContent ? "Update Event Notes" : "Add Event Notes"}
          </DialogTitle>

          <Close onClick={handleClose} sx={{ mr: 3, cursor: "pointer" }} />
        </Box>

        <DialogContent>
          <RichTextEditor
            initialContent={notesContent || ""}
            onSave={handleSaveNotes}
            title="What Went Wrong / Immediate Steps"
            placeholder="Describe what went wrong and your immediate action steps here..."
            saveButtonText={notesContent ? "Update" : "Save"}
          />
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default NotesCard;